import { api } from "@/api/client";
import { unwrap } from "@/api/errors";
import { uploadAsset } from "@/api/assets";
import type { ThreeDAsset } from "./threeDState";
import { MAX_GLB_UPLOAD_BYTES, MAX_GLB_PREVIEW_BYTES, GLB_MIME, ThreeDModelError, inspectViewerGLB } from "./viewer/modelPolicy";

/** Browse existing custody-backed images. Selection never creates or admits a 3D asset. */
export async function listThreeDReferenceAssets(projectID?: string, cursor?: string, signal?: AbortSignal) {
  return unwrap(await api.GET("/v1/assets", {
    params: { query: { media_type: "image", status: "available", sort: "newest", limit: 24,
      ...(projectID ? { project_id: projectID } : {}), ...(cursor ? { cursor } : {}) } }, signal,
  }));
}

export async function listThreeDModels(projectID?: string, cursor?: string, signal?: AbortSignal) {
  return unwrap(await api.GET("/v1/assets", { params: { query: { media_type: "3d", status: "available", integrity_state: "verified", sort: "newest", limit: 24,
    ...(projectID ? { project_id: projectID } : {}), ...(cursor ? { cursor } : {}) } }, signal }));
}

export async function getThreeDAsset(id: string, signal?: AbortSignal) {
  return unwrap(await api.GET("/v1/assets/{id}", { params: { path: { id } }, signal }));
}

export async function getThreeDDownload(asset: ThreeDAsset) {
  const result = unwrap(await api.POST("/v1/assets/bulk/download", { body: { asset_ids: [asset.id] } }));
  const item = result.items.find(item => item.id === asset.id);
  if (!item) throw new ThreeDModelError("The original is not available to download.");
  return { name: item.name, url: privateModelURL(item.url, asset).href };
}

function privateModelURL(value: string, asset: ThreeDAsset): URL {
  const url = new URL(value, window.location.origin);
  if ((url.protocol !== "https:" && !(url.protocol === "http:" && ["localhost", "127.0.0.1"].includes(url.hostname))) || url.username || url.password || url.hash || url.searchParams.get("asset_id") !== asset.id || url.searchParams.get("space_id") !== asset.space_id || url.searchParams.get("object") !== "original") throw new ThreeDModelError("The model’s private download could not be verified.");
  return url;
}

/** Use the same private custody flow as Assets. No local file is rendered first. */
export async function uploadThreeDModel(file: File, rightsAttested: boolean): Promise<ThreeDAsset> {
  if (!rightsAttested) throw new ThreeDModelError("Confirm you own this model or have permission to use it.");
  if (!file.name.toLowerCase().endsWith(".glb") || file.size < 28 || file.size > MAX_GLB_UPLOAD_BYTES) throw new ThreeDModelError("Choose a GLB file up to 128 MiB.");
  const header = new DataView(await file.slice(0, 12).arrayBuffer());
  if (header.byteLength !== 12 || header.getUint32(0, true) !== 0x46546c67 || header.getUint32(4, true) !== 2 || header.getUint32(8, true) !== file.size) throw new ThreeDModelError("This file does not have a valid GLB header.");
  // Browser MIME can be blank/octet-stream. The canonical helper infers GLB;
  // only the server's complete-upload inspection establishes asset authority.
  return uploadAsset(file, { visibility: "private", rightsAttested, rightsBasis: "owned_or_licensed" });
}

export function modelAssetProblem(asset: ThreeDAsset): string | undefined {
  if (asset.media_type !== "3d" || asset.mime_type !== GLB_MIME) return "Choose a verified GLB model.";
  if (asset.status !== "available" || asset.integrity_state !== "verified" || !asset.integrity_digest || asset.version < 1) return "This model is not ready to open.";
  if (!asset.can_download) return "Original-file access is needed to view this model.";
  if (asset.size_bytes < 28 || asset.size_bytes > MAX_GLB_UPLOAD_BYTES) return "This model exceeds the supported size.";
  return undefined;
}

/** Retrieve only the API-authorized immutable original, never a supplied URL. */
export async function readThreeDModel(asset: ThreeDAsset, signal: AbortSignal): Promise<ArrayBuffer> {
  const current = await getThreeDAsset(asset.id, signal);
  const problem = modelAssetProblem(current);
  if (problem) throw new ThreeDModelError(problem);
  if (current.id !== asset.id || current.space_id !== asset.space_id || current.version !== asset.version || current.integrity_digest !== asset.integrity_digest) throw new ThreeDModelError("This model changed. Choose it again from Assets.");
  if (current.size_bytes > MAX_GLB_PREVIEW_BYTES) throw new ThreeDModelError("Browser preview supports models up to 64 MiB. The original remains in Assets.");
  if (!current.url) throw new ThreeDModelError("The model’s private download is not available. Try again.");
  const url = privateModelURL(current.url, asset);
  // The short-lived capability carries delivery authority and is rechecked
  // by the CDN against the API. Do not forward browser cookies to its host.
  const response = await fetch(url, { signal, credentials: "omit", redirect: "error", cache: "no-store", referrerPolicy: "no-referrer" });
  if (!response.ok || !response.body) throw new ThreeDModelError("The model couldn’t download. Refresh its private access and try again.");
  const size = Number(response.headers.get("Content-Length"));
  if (size > MAX_GLB_PREVIEW_BYTES) { await response.body.cancel(); throw new ThreeDModelError("This model exceeds the browser preview limit."); }
  const reader = response.body.getReader(); const chunks: Uint8Array[] = []; let total = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read(); if (done) break;
      total += value.byteLength;
      if (total > current.size_bytes || total > MAX_GLB_PREVIEW_BYTES) throw new ThreeDModelError("The downloaded model exceeds its verified size.");
      chunks.push(value);
    }
  } catch (error) { await reader.cancel().catch(() => undefined); throw error; }
  finally { reader.releaseLock(); }
  if (total !== current.size_bytes) throw new ThreeDModelError("The model download is incomplete. Try again.");
  const bytes = new Uint8Array(total); let offset = 0;
  for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
  const digest = Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", bytes))).map(value => value.toString(16).padStart(2, "0")).join("");
  if (digest !== current.integrity_digest?.toLowerCase()) throw new ThreeDModelError("The model’s file integrity could not be verified.");
  if (signal.aborted) throw new DOMException("Aborted", "AbortError");
  inspectViewerGLB(bytes.buffer);
  return bytes.buffer;
}
